import React, { memo, useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import IconMC from '../../../components/IconMC';

/**
 * Formatea los segundos restantes en la cuenta regresiva FEP:
 * - > 24h: "FEP - Gratis - XXhrs"
 * - 1h - 24h: "FEP - Gratis - Xh Ym"
 * - < 1h: "FEP - Gratis - Xm"
 */
function formatFepTime(secondsRemaining, t) {
  if (!secondsRemaining || secondsRemaining <= 0) return null;

  const totalHours = Math.floor(secondsRemaining / 3600);
  const remainingMinutes = Math.floor((secondsRemaining % 3600) / 60);

  if (totalHours >= 24) {
    const days = Math.floor(totalHours / 24);
    if (days === 1) {
      return t('fepBadge.singleDayHours', { hours: totalHours, days });
    }
    return t('fepBadge.hours', { hours: totalHours, days });
  }
  if (totalHours >= 1) {
    return t('fepBadge.hoursMins', {
      hours: totalHours,
      mins: remainingMinutes
    });
  }
  const mins = Math.max(1, remainingMinutes);
  return t('fepBadge.mins', { mins });
}

/**
 * Componente FepBadgeMC
 * Renderiza la etiqueta indicadora de ventana gratuita FEP de Meta.
 */
const FepBadgeMC = memo(({ conversation, className = '' }) => {
  const { t } = useTranslation('InboxMC');

  const fepActive = conversation?.fep_active || conversation?.fepActive;
  const rawExpiresAt = conversation?.fep_window_expires_at || conversation?.fepWindowExpiresAt;
  const initialSeconds = conversation?.fep_expires_in_seconds ?? conversation?.fepExpiresInSeconds;

  const [secondsRemaining, setSecondsRemaining] = useState(() => {
    if (initialSeconds != null) return initialSeconds;
    if (rawExpiresAt) {
      const expMs = Date.parse(rawExpiresAt);
      return Math.max(0, Math.floor((expMs - Date.now()) / 1000));
    }
    return 0;
  });

  useEffect(() => {
    if (rawExpiresAt) {
      const expMs = Date.parse(rawExpiresAt);
      const calc = () => Math.max(0, Math.floor((expMs - Date.now()) / 1000));
      setSecondsRemaining(calc());

      const interval = setInterval(() => {
        const remaining = calc();
        setSecondsRemaining(remaining);
        if (remaining <= 0) {
          clearInterval(interval);
        }
      }, 30000); // Actualiza cada 30s

      return () => clearInterval(interval);
    }
    if (initialSeconds != null) {
      setSecondsRemaining(initialSeconds);
    }
  }, [rawExpiresAt, initialSeconds]);

  const badgeText = useMemo(() => formatFepTime(secondsRemaining, t), [secondsRemaining, t]);

  if (!fepActive || !badgeText || secondsRemaining <= 0) {
    return null;
  }

  return (
    <span
      className={`tw-inline-flex tw-items-center tw-gap-1 tw-w-fit tw-self-start tw-px-2 tw-py-0.5 tw-rounded tw-text-xs tw-font-semibold tw-bg-emerald-100 tw-text-emerald-800 tw-border tw-border-emerald-300 tw-whitespace-nowrap ${className}`}
      title={t('fepBadge.tooltip')}
    >
      <IconMC name="clock-outline" size="sm" className="tw-text-emerald-800" />
      {badgeText}
    </span>
  );
});

FepBadgeMC.displayName = 'FepBadgeMC';

export default FepBadgeMC;
